import { useMemo } from 'react';
import { useMultiStyleConfig } from '@chakra-ui/react';
import Box from '../Box/Box';
import Icon, { IconProps } from '../Icon/Icon';
import Text, { TextProps } from '../Text/Text';
import Divider, { DividerProps } from '../Divider/Divider';
import Link, { LinkProps } from '../Link/Link';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';

type ProgressStage = {
  label: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isSkipped?: boolean;
  tooltip?: Omit<TooltipProps, 'children'>;
  helperText?: string;
  action?: {
    onClick?: () => void;
    href?: LinkProps['href'];
    isExternal?: LinkProps['isExternal'];
  };
};
export type ProgressIndicatorProps = {
  stages: ProgressStage[];
  activeStageIndex: number;
} & (
  | {
      variant: 'horizontal';
      extendLines?: boolean;
    }
  | {
      variant: 'vertical';
    }
);

type RenderStage = (ProgressStage & { spacer?: never }) | { spacer: true };

const SegmentLine = ({
  activeSegmentIndex,
  segmentIndex,
  spacer,
  variant,
}: {
  segmentIndex: number;
  activeSegmentIndex: number;
  variant: 'horizontal' | 'vertical';
  spacer?: true;
}) => {
  const style = useMultiStyleConfig('ProgressIndicator', { variant });
  let props: {
    color: DividerProps['color'];
    variant?: DividerProps['variant'];
  } = { color: 'border.minimal' };

  if (segmentIndex < activeSegmentIndex) {
    props = { ...props, color: 'border.selected' };
  } else if (segmentIndex === activeSegmentIndex) {
    props = {
      ...props,
      color: 'border.selected',
      variant: 'dashed',
    };
  }
  return (
    <Divider
      gridColumn={spacer ? '1/4' : undefined}
      orientation={variant === 'horizontal' ? 'horizontal' : 'vertical'}
      size="2"
      sx={style.line}
      {...props}
    />
  );
};

const RealStage = ({
  activeSegmentIndex,
  extendLines,
  lastBlock,
  segmentIndex,
  stage: { action, tooltip, helperText, isDisabled, isInvalid, isSkipped, label },
  variant,
}: Pick<ProgressIndicatorProps, 'variant'> & {
  segmentIndex: number;
  activeSegmentIndex: number;
  stage: ProgressStage;
  lastBlock: boolean;
  extendLines: boolean;
}) => {
  let iconName: IconProps['name'];
  let iconColor: IconProps['color'] = 'icon.interactive';
  let textColor: TextProps['color'] = 'text.secondary';
  let iconTransform: IconProps['transform'];

  if (segmentIndex < activeSegmentIndex) {
    iconName = 'CheckCircleFilled';
  } else if (segmentIndex === activeSegmentIndex) {
    iconName = 'CircleHalfFilled';
    if (variant === 'vertical') iconTransform = 'rotate(90deg)';
    textColor = 'text.primary';
  } else {
    iconName = 'CircleDashed';
    iconColor = 'icon.tertiary';
  }
  if (isInvalid) {
    iconName = 'ErrorCircle';
    iconColor = 'icon.negative';
  }
  if (isDisabled) {
    iconColor = 'icon.disabled';
    textColor = 'text.disabled';
  }
  if (isSkipped) {
    iconName = 'SkipCircle';
    iconColor = 'icon.disabled';
    textColor = 'text.disabled';
  }
  const style = useMultiStyleConfig('ProgressIndicator', { variant });

  return (
    <>
      <Box sx={style.iconWithLine}>
        {variant === 'horizontal' &&
          (segmentIndex > 0 ? (
            <SegmentLine
              activeSegmentIndex={activeSegmentIndex}
              segmentIndex={segmentIndex - (extendLines ? 2 : 1)}
              variant={variant}
            />
          ) : (
            <div />
          ))}
        <Tooltip isDisabled={!tooltip} {...tooltip}>
          <Icon color={iconColor} name={iconName} transform={iconTransform} />
        </Tooltip>
        {lastBlock ? (
          <div />
        ) : (
          <SegmentLine activeSegmentIndex={activeSegmentIndex} segmentIndex={segmentIndex} variant={variant} />
        )}
      </Box>
      <Box sx={style.labelContainer}>
        {action && !isDisabled ? (
          <Link
            as={action.href ? 'a' : 'button'}
            colorScheme="purple"
            href={action.href}
            isExternal={action.isExternal}
            onClick={action.onClick}
            size="2"
            sx={style.label}
          >
            {label}
          </Link>
        ) : (
          <Text color={textColor} size="2" sx={style.label}>
            {label}
          </Text>
        )}
        {variant === 'vertical' && helperText && (
          <Text color="text.secondary" fontSize="1" lineHeight="1rem">
            {helperText}
          </Text>
        )}
      </Box>
    </>
  );
};

const ProgressIndicator = ({ activeStageIndex, stages, ...rest }: ProgressIndicatorProps): JSX.Element => {
  const style = useMultiStyleConfig('ProgressIndicator', { variant: rest.variant });
  const extendLines = Boolean(rest.variant === 'horizontal' && rest.extendLines);
  const activeSegmentIndex = extendLines ? activeStageIndex * 2 : activeStageIndex;

  const renderStages = useMemo((): RenderStage[] => {
    if (!extendLines) return stages;
    const extendedStages: RenderStage[] = stages.map((stage) => ({ ...stage, type: 'real' }));
    for (let i = 1; i < stages.length * 2 - 1; i += 2) {
      extendedStages.splice(i, 0, { spacer: true });
    }
    return extendedStages;
  }, [extendLines, stages]);
  return (
    <Box sx={style.container}>
      {renderStages.map((stage, segmentIndex, array) => {
        const lastBlock = segmentIndex === array.length - 1;

        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={segmentIndex}
            flex="1"
            sx={style.stage}
          >
            {!stage.spacer && (
              <RealStage
                activeSegmentIndex={activeSegmentIndex}
                extendLines={Boolean(extendLines)}
                lastBlock={lastBlock}
                segmentIndex={segmentIndex}
                stage={stage}
                variant={rest.variant}
              />
            )}
            {stage.spacer && (
              <Box sx={style.iconWithLine}>
                <SegmentLine
                  activeSegmentIndex={activeSegmentIndex}
                  segmentIndex={segmentIndex - 1}
                  spacer
                  variant="horizontal"
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProgressIndicator;
